/**
 * Earnings Trajectory — a compact dual-line sparkline that turns the
 * 15m → 5y → 10y arc into a visual wage-growth story rather than three
 * stacked numbers. Course line (Oxford navy) vs national-subject
 * benchmark (plum), with tooltip revealing the exact £ at each tier.
 *
 * Renders only when we have at least two of the three course-level
 * tiers so the line has something to arc between; benchmark line only
 * draws when the `subject` block from the benchmarks endpoint carries
 * a matching tier — otherwise the course line stands alone.
 *
 * UK-only for now: 15m comes from Discover Uni GOSALARY / HESA 15-mo
 * median, 5y/10y from DfE LEO. US degrees have a different cadence
 * (start / 3y / 10y USD) already covered by `UsEarningsComparison`
 * and `Career10yrCard`.
 */

import { Card } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ReferenceDot,
} from "recharts";
import ChartVerdict from "./ChartVerdict";

const C_COURSE   = "#002147";  // Oxford navy — this course
const C_SUBJECT  = "#7C3AED";  // plum — national-subject benchmark

const fmtGBP = (n) => (typeof n === "number"
  ? `£${Math.round(n).toLocaleString()}`
  : "—");
const fmtGBPk = (n) => (typeof n === "number"
  ? `£${Math.round(n / 1000)}k`
  : "");

// Pull a course value at each tier, falling back to the same order the
// UkCareer10yCard uses so we tell one consistent number story.
function courseTiers(degree) {
  const s15 = (degree?.grad_outcomes_salary?.median_gbp)
    ?? (degree?.hesa_stats?.salary_median_gbp);
  const s5  = degree?.leo?.salary_5y;
  const s10 = degree?.leo?.salary_10y ?? degree?.outcomes?.median_earnings_yr10_gbp;
  return { s15, s5, s10 };
}

function benchmarkTiers(benchmarks) {
  const s = benchmarks?.subject?.earnings || {};
  const n = benchmarks?.national?.earnings || {};
  // Prefer subject-level (matches the "national, this subject" story
  // parents actually care about) — fall back to national when the
  // subject bucket is thin (<5 courses, so `subject` is null upstream).
  const pick = (subjKey, natKey) => (
    (typeof s[subjKey] === "number" ? s[subjKey] : null)
    ?? (typeof n[natKey] === "number" ? n[natKey] : null)
  );
  return {
    b15: pick("earnings_15m_gbp", "earnings_15m_gbp"),
    b5:  pick("earnings_5y_gbp",  "earnings_5y_gbp"),
    b10: pick("earnings_10y_gbp", "earnings_10y_gbp"),
    // Which one did we actually resolve? (drives the legend copy)
    scope: benchmarks?.subject?.earnings ? "subject" : "national",
    n_subject:  benchmarks?.n_subject,
    n_national: benchmarks?.n_national,
    cah_name:   benchmarks?.subject?.cah_name,
  };
}

// Tooltip — one row per line, showing the course value and the
// benchmark side-by-side at the hovered tier.
function TrajectoryTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-none border border-stone-200 bg-white shadow-sm px-3 py-2 text-[11px] font-mono">
      <div className="uppercase tracking-wider text-stone-500 mb-1">{label} out</div>
      <div className="flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-sm" style={{ background: C_COURSE }} />
        <span className="text-stone-600">This course</span>
        <span className="ml-auto text-stone-900 font-semibold">{fmtGBP(row.course)}</span>
      </div>
      {typeof row.benchmark === "number" ? (
        <div className="flex items-center gap-2 mt-0.5">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: C_SUBJECT }} />
          <span className="text-stone-600">{row.benchmarkLabel}</span>
          <span className="ml-auto text-stone-900 font-semibold">{fmtGBP(row.benchmark)}</span>
        </div>
      ) : null}
      {typeof row.benchmark === "number" && typeof row.course === "number" ? (
        <div className="mt-1 pt-1 border-t border-stone-100 text-[10px] text-stone-500 uppercase tracking-wider">
          Course {row.course >= row.benchmark ? "+" : ""}{fmtGBPk(row.course - row.benchmark)} vs benchmark
        </div>
      ) : null}
    </div>
  );
}

export default function EarningsTrajectoryCard({ degree, benchmarks }) {
  const country = (degree?.country
    || (degree?.university_id?.startsWith("uk-") ? "UK" : "")).toUpperCase();
  if (country !== "UK") return null;

  const { s15, s5, s10 } = courseTiers(degree);
  const b = benchmarkTiers(benchmarks);
  const courseValues  = [s15, s5, s10].filter((v) => typeof v === "number");
  if (courseValues.length < 2) return null;   // no arc → no story

  const benchmarkLabel = b.scope === "subject"
    ? (b.cah_name ? `National · ${b.cah_name}` : "National · this subject")
    : "National · all UK grads";

  const data = [
    { label: "15m",  course: s15, benchmark: b.b15, benchmarkLabel },
    { label: "5y",   course: s5,  benchmark: b.b5,  benchmarkLabel },
    { label: "10y",  course: s10, benchmark: b.b10, benchmarkLabel },
  ];

  // Domain padding so the top-most dot never crops against the frame.
  const all = [...courseValues,
               ...[b.b15, b.b5, b.b10].filter((v) => typeof v === "number")];
  const lo  = Math.floor(Math.min(...all) * 0.85 / 1000) * 1000;
  const hi  = Math.ceil(Math.max(...all) * 1.10 / 1000) * 1000;

  const growth = (s10 && s15) ? ((s10 - s15) / s15) * 100 : null;

  // Verified badge — inherits the same convention the sibling cards use.
  const src = degree?.leo?.data_source
    || degree?.leo?.salary_5y_source
    || (degree?.grad_outcomes_salary?.data_source && "Discover Uni + DfE LEO")
    || "seed_estimate";
  const yr  = degree?.leo?.data_year ?? 2024;
  const isVerified = src && src !== "seed_estimate";

  return (
    <Card
      data-testid="earnings-trajectory-card"
      className="p-6 border border-stone-200 rounded-none shadow-none bg-white"
    >
      <div className="text-[10px] font-underground-sc tracking-wider text-[#7C3AED] mb-1">
        Earnings trajectory
      </div>
      <h3 className="font-editorial text-xl text-stone-900 leading-tight">
        The wage-growth arc, 15 months to 10 years out
      </h3>
      <p className="text-xs text-stone-500 mt-1 mb-4">
        Median gross earnings at each career checkpoint, plotted against
        the {b.scope === "subject" ? "national-subject" : "national UK graduate"} benchmark
        so families can see whether this course out-earns its peers over time —
        not just at graduation.
      </p>

      {/* Headline growth line — one sentence that names the arc */}
      {typeof growth === "number" ? (
        <div className="flex items-baseline gap-3 mb-3" data-testid="earnings-growth-headline">
          <div className="font-serif text-4xl text-[#002147]">
            {growth >= 0 ? "+" : ""}{growth.toFixed(0)}%
          </div>
          <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 leading-tight">
            <div>Earnings growth</div>
            <div className="normal-case tracking-normal text-stone-500">
              {fmtGBP(s15)} → {fmtGBP(s10)} across 10 years
            </div>
          </div>
        </div>
      ) : null}

      {/* The sparkline itself — compact height, no cartesian noise */}
      <div className="h-[180px] w-full" data-testid="earnings-trajectory-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 12, right: 16, left: 4, bottom: 4 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="#f1f5f9"
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#78716c", fontFamily: "monospace" }}
              tickLine={false}
              axisLine={{ stroke: "#e7e5e4" }}
            />
            <YAxis
              domain={[lo, hi]}
              tick={{ fontSize: 10, fill: "#a8a29e", fontFamily: "monospace" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={fmtGBPk}
              width={44}
            />
            <Tooltip content={<TrajectoryTooltip />} cursor={{ stroke: "#e7e5e4" }} />
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke={C_SUBJECT}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={{ r: 3, stroke: C_SUBJECT, fill: "#fff", strokeWidth: 1.5 }}
              activeDot={{ r: 5, stroke: C_SUBJECT, fill: C_SUBJECT }}
              connectNulls
              isAnimationActive={false}
              name={benchmarkLabel}
            />
            <Line
              type="monotone"
              dataKey="course"
              stroke={C_COURSE}
              strokeWidth={2.5}
              dot={{ r: 4, stroke: C_COURSE, fill: "#fff", strokeWidth: 2 }}
              activeDot={{ r: 6, stroke: C_COURSE, fill: C_COURSE }}
              connectNulls
              isAnimationActive={false}
              name="This course"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend — mirrors the tooltip colour convention */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-underground-sc tracking-wider text-stone-500 mt-2">
        <span className="inline-flex items-center gap-1.5" data-testid="earnings-legend-course">
          <span className="inline-block w-4 h-[2px]" style={{ background: C_COURSE }} />
          This course
        </span>
        <span className="inline-flex items-center gap-1.5" data-testid="earnings-legend-benchmark">
          <span
            className="inline-block w-4 h-[2px]"
            style={{
              background: `repeating-linear-gradient(to right, ${C_SUBJECT} 0 4px, transparent 4px 7px)`,
            }}
          />
          {benchmarkLabel}
          {b.scope === "subject" && typeof b.n_subject === "number"
            ? ` (n=${b.n_subject.toLocaleString()})`
            : b.scope === "national" && typeof b.n_national === "number"
              ? ` (n=${b.n_national.toLocaleString()})`
              : ""}
        </span>
      </div>

      {/* One-line verdict — reads the growth arc and the 10y gap vs
          benchmark and turns them into a family-scan takeaway. */}
      {(() => {
        if (typeof growth !== "number") return null;
        const gap10 = (typeof s10 === "number" && typeof b.b10 === "number")
          ? s10 - b.b10 : null;
        const bandTone = growth >= 60 ? "positive" : growth >= 25 ? "neutral" : "warning";
        const arcCopy = `Salaries climb ${growth >= 0 ? "+" : ""}${growth.toFixed(0)}% from ${fmtGBP(s15)} to ${fmtGBP(s10)} over 10 years`;
        const benchCopy = gap10 == null
          ? ""
          : gap10 >= 500
            ? ` — beating the ${b.scope === "subject" ? "subject" : "UK-grad"} benchmark by ~${fmtGBP(gap10)} at year 10.`
            : gap10 <= -500
              ? ` — lagging the ${b.scope === "subject" ? "subject" : "UK-grad"} benchmark by ~${fmtGBP(Math.abs(gap10))} at year 10.`
              : ` — tracking the ${b.scope === "subject" ? "subject" : "UK-grad"} benchmark closely at year 10.`;
        return <ChartVerdict testId="verdict-earnings-trajectory" text={`${arcCopy}${benchCopy}`} tone={bandTone} />;
      })()}

      {/* Footer — verified pill + one-line source attribution */}
      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-stone-100">
        <span className="text-[10px] font-underground-sc tracking-wider text-stone-400">
          15m: Discover Uni GOSALARY · 5y/10y: DfE LEO
        </span>
        <span
          data-testid={`earnings-source-pill-${isVerified ? "verified" : "seed"}`}
          title={isVerified
            ? `${src}${yr ? ` (${yr})` : ""}`
            : "Seeded estimate — real-source feed pending"}
          className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-underground-sc tracking-wider ${
            isVerified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
          }`}
        >
          {isVerified
            ? `Verified · ${String(src).replace(/\s*\d{4,6}(?:[/-]\d{2,4})?\s*$/, "").slice(0, 24)}${yr ? ` ${yr}` : ""}`
            : "Seeded estimate"}
        </span>
      </div>
    </Card>
  );
}
